import Joi from 'joi';
import api from '../api';
import { paginationSchema } from '../cortizol/schemas';

const specialCollectionSchema = Joi.object({
  id: Joi.string().required(),
  name: Joi.string().required(),
  address: Joi.object({
    streetNumber: Joi.string().allow(null, ''),
    streetName: Joi.string().required(),
    postalCode: Joi.string().allow(null, ''),
    city: Joi.string().allow(null, ''),
  }).required(),
  startDate: Joi.string().required(),
  endDate: Joi.string().required(),
  startTime: Joi.string().required(),
  endTime: Joi.string().required(),
  description: Joi.string().allow(null, ''),
  publishedDate: Joi.string().allow(null, ''),
  latitude: Joi.number().allow(null, ''),
  longitude: Joi.number().allow(null, ''),
});

/**
 * @param {object} queryParams
 * @param {number} queryParams.pageNumber
 * @param {number} queryParams.pageSize
 * @returns {Promise<{data: {specialCollectionPoints: SpecialCollection[], pagination: Pagination}}>}
 */
export const getSpecialCollectionsList = async (queryParams) => {
  const result = await api.get(
    '/old-tires/v1-0/special-collection-points',
    { params: queryParams },
  );

  try {
    const schema = Joi.object({
      specialCollectionPoints: Joi.array().items(specialCollectionSchema).required(),
      pagination: paginationSchema,
    });

    await schema.validateAsync(result.data, { allowUnknown: true });
  } catch (error) {
    throw new Error(`API response does not match Garage Exclusions List Schema: ${error.message}`);
  }

  return result.data;
};

/**
 * Get a specialCollection.
 * @param {string} id
 * @returns {Promise<SpecialCollection>}
 */
export const getSpecialCollection = async (id) => {
  const result = await api.get(`/old-tires/v1-0/special-collection-points/${id}`);

  try {
    await specialCollectionSchema.validateAsync(
      result.data.specialCollectionPoint,
      { allowUnknown: true },
    );
  } catch (error) {
    throw new Error(`API response does not match Special Collection Schema: ${error.message}`);
  }

  return result.data.specialCollectionPoint;
};

/**
 * Post to create a specialCollection.
 * @param {object} specialCollection
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export const postCreateSpecialCollection = async (specialCollection) => api.post(
  '/old-tires/v1-0/special-collection-points',
  specialCollection,
);

/**
 * Put to update a specialCollection.
 * @param {SpecialCollection} specialCollection
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export const putUpdateSpecialCollection = async (specialCollection) => api.put(
  `/old-tires/v1-0/special-collection-points/${specialCollection.id}`,
  specialCollection,
);

/**
 * Delete a specialCollection.
 * @param {string} id
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export const deleteSpecialCollection = async (id) => api.delete(
  `/old-tires/v1-0/special-collection-points/${id}`,
);
